import { useEffect, useState } from "react";
import { Archive, ArrowRight, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { MobileShell } from "@/components/MobileShell";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { api, getApiError } from "@/lib/api";


export default function LeftoversPage() {
  const navigate = useNavigate();
  const [leftovers, setLeftovers] = useState({ items: [], summary: {} });

  useEffect(() => {
    const fetchLeftovers = async () => {
      const response = await api.get("/leftovers");
      setLeftovers(response.data);
    };

    fetchLeftovers();
  }, []);

  const handleUsePortion = async (leftoverId) => {
    try {
      const response = await api.post(`/leftovers/${leftoverId}/use`, { portions_used: 1 });
      setLeftovers(response.data);
      toast.success("Één leftover-portie gebruikt.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const deleteLeftover = async (leftoverId) => {
    try {
      const response = await api.delete(`/leftovers/${leftoverId}`);
      setLeftovers(response.data);
      toast.success("Leftover verwijderd.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  return (
    <MobileShell title="Leftovers" eyebrow="Bewaar, gebruik, voorkom verspilling" action={<Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={() => navigate("/planner")} data-testid="leftovers-open-planner-button">Planner</Button>}>
      <div className="space-y-4" data-testid="leftovers-page">
        <section className="rounded-[28px] border border-stone-200 bg-[linear-gradient(135deg,_rgba(243,230,198,0.9),_rgba(236,245,234,0.95))] p-4" data-testid="leftovers-summary-card">
          <div className="flex items-start gap-3">
            <Archive className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="leftovers-summary-title">
                Actieve leftovers voor later in de week
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-white/85 px-3 py-1 text-stone-700" data-testid="leftovers-total-items-pill">{leftovers.summary.total_items || 0} items</span>
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid="leftovers-total-portions-pill">{leftovers.summary.total_portions || 0} porties</span>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800" data-testid="leftovers-expiring-pill">{leftovers.summary.expiring_soon_count || 0} bijna op datum</span>
              </div>
            </div>
          </div>
        </section>

        {leftovers.items.map((item, index) => (
          <section key={item.id} className="stagger-enter rounded-[28px] border border-stone-200 bg-white/90 p-4" style={{ animationDelay: `${index * 60 + 60}ms` }} data-testid={`leftover-item-${item.id}`}>
            <div className="flex items-start gap-3">
              <img src={item.recipe_thumbnail_url} alt={item.recipe_title} className="h-20 w-20 rounded-[20px] object-cover" data-testid={`leftover-image-${item.id}`} />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid={`leftover-best-before-${item.id}`}>
                  Best before {item.best_before}
                </p>
                <h2 className="mt-1 text-lg font-semibold text-stone-900" data-testid={`leftover-title-${item.id}`}>
                  {item.recipe_title}
                </h2>
                <p className="mt-2 text-sm text-stone-600" data-testid={`leftover-description-${item.id}`}>
                  {item.recipe_description}
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid={`leftover-portions-${item.id}`}>
                    {item.portions_remaining} porties over
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Button className="h-12 rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={() => handleUsePortion(item.id)} data-testid={`leftover-use-button-${item.id}`}>
                Gebruik 1 portie
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="h-12 rounded-full border-red-200 bg-red-50 text-red-700 hover:bg-red-100" onClick={() => deleteLeftover(item.id)} data-testid={`leftover-delete-button-${item.id}`}>
                <Trash2 className="mr-2 h-4 w-4" />
                Verwijder
              </Button>
            </div>
          </section>
        ))}

        {!leftovers.items.length ? (
          <section className="rounded-[28px] border border-dashed border-stone-300 bg-white/75 p-5" data-testid="leftovers-empty-state">
            <p className="text-sm text-stone-600" data-testid="leftovers-empty-text">
              Nog geen leftovers. Rond een maaltijd af in cook mode en bewaar extra porties voor later.
            </p>
            <Button className="mt-4 rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={() => navigate("/planner")} data-testid="leftovers-empty-planner-button">
              Naar planner
            </Button>
          </section>
        ) : null}
      </div>
    </MobileShell>
  );
}